import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { PageShell } from "../components/Layout";
import CosmicNightHero from "../components/CosmicNightHero";
import SEO from "../components/SEO";
import api, { resolveAssetUrl } from "../lib/api";
import { displayedRating, displayedReviewsCount } from "../lib/astrologerDisplay";
import { useCurrency } from "../context/CurrencyContext";
import { useAuth } from "../context/AuthContext";
import { startGoogleSignIn } from "../lib/capacitorAuth";
import { Star, MagnifyingGlass, Sparkle, ChatCircleDots, Phone, Lightning, ArrowRight, X, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { getAdSessionId } from "../lib/adAttribution";

const SPECS = ["All", "Vedic", "Nadi", "KP", "Tarot", "Numerology", "Palmistry", "Vastu"];
const LANGS = ["All", "English", "Hindi", "Tamil", "Telugu", "Bengali", "Marathi"];
// Min-years-of-experience options. 0 == "Any".
const EXP_BUCKETS = [
  { value: 0,  label: "Any experience" },
  { value: 5,  label: "5+ years" },
  { value: 10, label: "10+ years" },
  { value: 15, label: "15+ years" },
  { value: 20, label: "20+ years" },
];

export default function AstrologersPage() {
  const { user } = useAuth();
  const { formatPrice, country } = useCurrency();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const spec = params.get("spec") || "All";
  const lang = params.get("lang") || "All";
  const exp  = Number(params.get("exp") || 0);
  const q    = params.get("q") || "";
  const category = params.get("category") || "all";
  const firstChatFree = !user?.free_trial_used;

  // US-audience category quick-filters. The 5 chips funnel the seeker to the
  // archetype they came looking for. Predicates use the same data that backs
  // `isPsychicArchetype` so the order of the grid stays sensible.
  // NOTE: chips are intersected with the regular spec/lang/exp/search filters,
  // so all existing filter UI keeps working when a category chip is active.
  const US_CATEGORIES = [
    { key: "all",      label: "All readers", color: "gold" },
    { key: "psychic",  label: "Psychics",    color: "violet" },
    { key: "tarot",    label: "Tarot reading", color: "rose" },
    { key: "vedic",    label: "Vedic astrologers", color: "saffron" },
    { key: "wealth",   label: "Wealth & finance", color: "emerald" },
    { key: "pastlife", label: "Past life",   color: "teal" },
    { key: "soulmate", label: "Soulmate",    color: "pink" },
  ];
  const matchesUSCategory = (a, key) => {
    if (key === "all") return true;
    const spec_ = (a.specialization || "").toLowerCase();
    const tech = (a.technique || "").toLowerCase();
    if (key === "psychic") {
      // Western-tropical psychics — the 4 dedicated US personas.
      return spec_ === "western astrology";
    }
    if (key === "tarot") {
      return spec_ === "tarot" || tech.includes("tarot");
    }
    if (key === "vedic") {
      return spec_ === "vedic";
    }
    if (key === "wealth") {
      // Money / career / business specialists across all traditions.
      return tech.includes("financial") || tech.includes("money")
        || tech.includes("wealth") || tech.includes("rajayoga")
        || tech.includes("royal karma") || tech.includes("business")
        || tech.includes("ashtakavarga") || tech.includes("varshaphal")
        || tech.includes("chaldean") || tech.includes("lo shu");
    }
    if (key === "pastlife") {
      return tech.includes("past life") || tech.includes("past-life")
        || tech.includes("d60") || tech.includes("south node");
    }
    if (key === "soulmate") {
      return tech.includes("twin flame") || tech.includes("twin-flame")
        || tech.includes("soulmate") || tech.includes("north node")
        || tech.includes("love") || tech.includes("relationship");
    }
    return true;
  };

  // US audience uses "psychic" mental model rather than "astrologer".
  // We keep the underlying directory + filters identical but:
  //   1) Swap user-facing labels & SEO copy to "psychics / readers".
  //   2) Promote Tarot + Past-Life readers to the top of the grid.
  // Indian and European audiences see the original astrologer-first
  // experience untouched.
  const isUS = country === "US";
  const T = isUS
    ? {
        heroH1: <>Talk to Psychics & Tarot Readers — <span className="text-gold">Get Real Answers in Seconds</span></>,
        heroSub: firstChatFree
          ? <>Your <strong className="text-white">first 3 minutes are on us</strong> — chat with a vetted psychic, tarot reader or past-life expert. Just open a chat.</>
          : <>Vetted psychics, tarot readers & spiritual experts — just open a chat to get started.</>,
        ctaPrimary: "First 3 Minutes Free — Tarot · Psychic · Past Life",
        seoTitle: "Talk to Psychics & Tarot Readers Online — Free First 3 Minutes",
        seoDesc: "Chat with vetted psychics, tarot card readers and past-life experts online at AstroKuber. First 3 minutes free with every reader. Real-time love, career and life guidance — Tarot Rider-Waite spreads, past-life D60 readings, palmistry and Vedic insights.",
        emptyMsg: "No psychics or tarot readers match these filters.",
        emptyCta: "Clear filters to see all readers",
        loadingMsg: "Loading psychics & tarot readers...",
      }
    : {
        heroH1: <>Talk to <span className="text-gold">Expert Vedic Specialist</span> &amp; Get Life Answers within Seconds</>,
        heroSub: firstChatFree
          ? <>Your <strong className="text-white">first 3 minutes are on us</strong> — chat with any AI specialist below and get answers grounded in your own kundali. Just open a chat.</>
          : <>Verified specialists — each reading grounded in your own kundali. Just open a chat.</>,
        ctaPrimary: "First 3 Minutes Free Life Reading",
        seoTitle: "Chat With Astrologers Free — Verified Vedic Specialists Online 24×7",
        seoDesc: "Chat with astrologers free at AstroKuber. Verified Vedic, KP, Nadi, Numerology, Palmistry and Vastu astrologers — first 3 minutes free, then ₹15–55/min. Instant answers on marriage, career, love, finance, health from classical techniques (D9 Navamsa, D10 Dasamsa, Tajaka, KP Sub-Lord, Kalachakra Dasha).",
        emptyMsg: "No astrologers match these filters.",
        emptyCta: "Clear filters to see all",
        loadingMsg: "Loading...",
      };

  // Full unfiltered directory — cached once so the typeahead suggestion
  // dropdown can search names instantly without waiting on the backend.
  const [allAstros, setAllAstros] = useState([]);
  // Controlled value of the search input. Kept independent of the URL `q`
  // so every keystroke shows suggestions immediately. We sync it back to
  // `q` (and therefore back to the backend filter) only when the user
  // commits a suggestion, presses Enter, or pauses typing.
  const [query, setQuery] = useState(q);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const searchBoxRef = useRef(null);
  // The suggestions dropdown is portalled to document.body so it lives outside
  // `searchBoxRef`. We need a separate ref to test outside-clicks against, or
  // the mousedown handler closes the dropdown BEFORE the Link receives its
  // click — making suggestions appear "unclickable".
  const suggestionsRef = useRef(null);
  // Cached input-rect → drives the portal-rendered dropdown's position so
  // it floats above the entire page (escapes any stacking context formed by
  // sibling card transitions).
  const [popRect, setPopRect] = useState(null);
  const updatePopRect = () => {
    const el = searchBoxRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPopRect({ top: r.bottom + 6, left: r.left, width: r.width });
  };
  useLayoutEffect(() => { if (suggestOpen) updatePopRect(); }, [suggestOpen]);
  useEffect(() => {
    if (!suggestOpen) return;
    window.addEventListener("scroll", updatePopRect, true);
    window.addEventListener("resize", updatePopRect);
    return () => {
      window.removeEventListener("scroll", updatePopRect, true);
      window.removeEventListener("resize", updatePopRect);
    };
  }, [suggestOpen]);

  // ─── Direct-action handlers for the new dual-button astrologer card.
  // The two buttons swallow the click so the parent <Link> doesn't also
  // navigate to the profile — tapping anywhere else on the tile still
  // opens the profile (default Link behaviour).
  const startChatFromCard = async (e, astro) => {
    e.preventDefault();
    e.stopPropagation();
    if (!astro?.chat_enabled && !astro?.is_ai) {
      toast.message(`${astro.name} isn't taking chats right now.`);
      return;
    }
    if (!user) {
      toast.info("Sign in to start a chat");
      // After sign-in, land back on the astrologer's detail page so the
      // user can finish the action.
      const redirectUrl = `${window.location.origin}/astrologers/${astro.astrologer_id}`;
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      startGoogleSignIn(redirectUrl);
      return;
    }
    try {
      const { data: chat } = await api.post("/chats", {
        astrologer_id: astro.astrologer_id,
        ad_session_id: getAdSessionId(),
      });
      navigate(`/chat/${chat.chat_id}`);
    } catch {
      toast.error("Could not start chat");
    }
  };

  const startCallFromCard = (e, astro) => {
    e.preventDefault();
    e.stopPropagation();
    if (!astro?.call_enabled) {
      toast.message(`${astro.name} isn't taking calls right now.`);
      return;
    }
    // The detail page owns the full Twilio Voice setup + PreCall modal.
    // `?action=call` makes it auto-open the modal on mount.
    navigate(`/astrologers/${astro.astrologer_id}?action=call`);
  };


  // One-time fetch of the entire directory for the typeahead dropdown.
  // Always pass `country` so the typeahead matches the visible grid.
  useEffect(() => {
    api.get("/astrologers", { params: country ? { country } : {} })
      .then(({ data }) => setAllAstros(data || []));
  }, [country]);

  useEffect(() => { setQuery(q); }, [q]);

  useEffect(() => {
    setLoading(true);
    api
      .get("/astrologers", {
        params: {
          specialization: spec,
          language: lang,
          q,
          ...(exp > 0 ? { min_experience: exp } : {}),
          ...(country ? { country } : {}),
        },
      })
      .then(({ data }) => {
        // Pin AI Guides on top, but preserve the backend's marketing-priority
        // ordering within each group (server.py applies the Acharya-Surekha
        // first, Rajat-Chopra second, …, sequence). A stable sort keeps that
        // sequence untouched whenever two cards share the same `is_ai` flag.
        const sorted = [...data].sort((a, b) => {
          if (!!b.is_ai !== !!a.is_ai) return b.is_ai ? 1 : -1;
          return 0;
        });
        setList(sorted);
      })
      .finally(() => setLoading(false));
  }, [spec, lang, exp, q, country]);

  // US audience prefers tarot + past-life readers. Re-order so anyone
  // matching those archetypes floats to the top, then everyone else in
  // the original directory order. The filter & search experience is
  // completely unchanged.
  const isPsychicArchetype = (a) => {
    if ((a.audience_country || "").toUpperCase() === "US") return true;
    const spec_ = (a.specialization || "").toLowerCase();
    const tech = (a.technique || "").toLowerCase();
    return (
      spec_ === "tarot"
      || tech.includes("tarot")
      || tech.includes("past life") || tech.includes("past-life")
      || tech.includes("d60")
      || spec_ === "palmistry"
    );
  };
  const displayList = useMemo(() => {
    // Live human astrologers (chat or call ON, is_ai=false) ALWAYS lead the
    // grid — this is the product promise to the human astrologer that the
    // moment they flip Chat ON in their portal, they're #1 of the directory.
    // Applies on BOTH Indian and US views (the live human still leads even
    // if the US visitor was primed to see psychic personas first).
    const liveHumans = list.filter((a) => !a.is_ai && (a.chat_enabled || a.call_enabled));
    const remainder = list.filter((a) => !(!a.is_ai && (a.chat_enabled || a.call_enabled)));

    if (!isUS) return [...liveHumans, ...remainder];
    // First narrow the remainder by category chip (if any).
    const filtered = category === "all"
      ? remainder
      : remainder.filter((a) => matchesUSCategory(a, category));
    // For US visitors, ranking inside the remainder is:
    //  1. The 5 US-only psychic personas (audience_country === "US")
    //  2. Other psychic-archetype readers (Tarot / Past-Life / Palmistry)
    //  3. Everyone else in original order
    const usOnly = filtered.filter((a) => (a.audience_country || "").toUpperCase() === "US");
    const otherPsy = filtered.filter((a) =>
      (a.audience_country || "").toUpperCase() !== "US" && isPsychicArchetype(a)
    );
    const rest = filtered.filter((a) =>
      (a.audience_country || "").toUpperCase() !== "US" && !isPsychicArchetype(a)
    );
    // Live humans always pinned, then US tiers.
    const pinnedLive = category === "all"
      ? liveHumans
      : liveHumans.filter((a) => matchesUSCategory(a, category));
    return [...pinnedLive, ...usOnly, ...otherPsy, ...rest];
  }, [list, isUS, category]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (key, value) => {
    const np = new URLSearchParams(params);
    if (!value || value === "All" || value === "0" || value === 0) np.delete(key);
    else np.set(key, value);
    setParams(np);
  };

  // Deterministic-from-astrologer_id pseudo-random availability:
  // ~70% Available, ~30% Busy. The same astrologer always shows the same
  // status within a session (so the user doesn't see flicker on refresh),
  // but the directory as a whole feels alive — some green, some red.
  // When the user clicks through to the detail page we force `Available`
  // so we never lose a chat to a "busy" badge at the conversion moment.
  //
  // EXCEPTION: For HUMAN astrologers, we honour the real `chat_enabled`
  // flag they flipped from their portal dashboard. Chat-enabled humans are
  // ALWAYS shown as available; chat-disabled humans are always busy. AI
  // astrologers keep using the deterministic stub.
  const isAvailable = (astrologer) => {
    if (typeof astrologer === "object" && astrologer !== null) {
      if (astrologer.is_ai === false) {
        return !!(astrologer.chat_enabled || astrologer.call_enabled);
      }
      return _avHash(astrologer.astrologer_id);
    }
    return _avHash(astrologer);
  };
  const _avHash = (astrologerId) => {
    if (!astrologerId) return true;
    let hash = 0;
    for (let i = 0; i < astrologerId.length; i++) {
      hash = ((hash * 31) + astrologerId.charCodeAt(i)) | 0;
    }
    return Math.abs(hash) % 10 < 7;
  };

  // Clear all filters at once (used by chip on the filter bar).
  const clearFilters = () => { setParams(new URLSearchParams()); setQuery(""); };
  const hasActiveFilters = spec !== "All" || lang !== "All" || exp > 0 || !!q || (isUS && category !== "all");

  // Case-insensitive substring match against the cached directory.
  // Also matches specialization & languages so "tamil" or "vedic" pull up
  // relevant astrologers even though it's labelled "Search name".
  const suggestions = useMemo(() => {
    if (!query.trim()) return [];
    const needle = query.trim().toLowerCase();
    const hits = allAstros.filter((a) => {
      const haystack = [
        a.name,
        a.specialization,
        ...(a.languages || []),
        a.technique,
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(needle);
    });
    // Prioritise name-start matches first, then any-substring.
    const startsWith = hits.filter((a) => a.name.toLowerCase().startsWith(needle));
    const others     = hits.filter((a) => !a.name.toLowerCase().startsWith(needle));
    return [...startsWith, ...others].slice(0, 8);
  }, [query, allAstros]);

  // Outside-click closes the suggestions dropdown. We must also treat clicks
  // inside the portalled dropdown as "inside" — otherwise the mousedown fires
  // first, the dropdown unmounts, and the suggestion's click event has no
  // target left to navigate to.
  useEffect(() => {
    if (!suggestOpen) return;
    const onClick = (e) => {
      const inBox = searchBoxRef.current && searchBoxRef.current.contains(e.target);
      const inSuggestions = suggestionsRef.current && suggestionsRef.current.contains(e.target);
      if (!inBox && !inSuggestions) {
        setSuggestOpen(false);
        setHighlight(-1);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [suggestOpen]);

  const commitSearch = (value) => {
    setSuggestOpen(false);
    setHighlight(-1);
    update("q", value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSuggestOpen(true);
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlight >= 0 && suggestions[highlight]) {
        navigate(`/astrologers/${suggestions[highlight].astrologer_id}`);
      } else {
        commitSearch(query);
      }
    } else if (e.key === "Escape") {
      setSuggestOpen(false);
      setHighlight(-1);
    }
  };

  return (
    <PageShell>
      <SEO
        title={T.seoTitle}
        description={T.seoDesc}
        path="/astrologers"
        keywords={[
          "chat with astrologers free", "free astrologer chat", "talk to astrologer free",
          "free online astrologer", "astrologer chat online free", "first chat free astrologer",
          "talk to astrologer online", "chat with astrologer", "online astrologer",
          "live astrologer chat", "indian astrologer", "vedic astrologer online",
          "best astrologer near me", "astrologer chat 24x7", "instant astrologer consultation",
          "nadi astrologer online", "kp astrologer online", "palmistry chat",
          "numerologist online", "vastu consultant online",
          "marriage astrologer chat", "career astrologer online", "love astrologer free",
          "kundali astrologer online", "astrologer for marriage timing", "rahu remedy astrologer",
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Product",
              "name": "Chat With Astrologers Online",
              "brand": { "@type": "Organization", "name": "AstroKuberChat", "url": "https://astrokuberchat.com" },
              "provider": { "@type": "Organization", "name": "AstroKuber", "url": "https://astrokuberchat.com" },
              "areaServed": "Worldwide",
              "category": "Online Vedic Astrology Consultation",
              "offers": {
                "@type": "AggregateOffer",
                "priceCurrency": "INR",
                "lowPrice": "0",
                "highPrice": "55",
                "description": "First 3 minutes free with every AI astrologer; then ₹15–55/min based on technique and seniority.",
              },
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": "4.9",
                "ratingCount": "4827",
                "bestRating": "5",
              },
            },
            {
              "@type": "ItemList",
              "name": "AstroKuber Astrologers Directory",
              "description": "Directory of verified Vedic astrologers covering Parashari, Jaimini, KP, Nadi, Tajaka, D9 Navamsa, D10 Dasamsa, Kalachakra Dasha, Bhrigu Bindu and Vastu.",
            },
            {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://astrokuberchat.com/" },
                { "@type": "ListItem", "position": 2, "name": "Astrologers", "item": "https://astrokuberchat.com/astrologers" },
              ],
            },
            {
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "Can I chat with astrologers for free on AstroKuber?",
                  "acceptedAnswer": { "@type": "Answer", "text": "Yes. Every new user gets 3 free minutes with the first AI astrologer they consult. After that, billing is per-minute based on the astrologer's rate (₹15 – ₹55/min). The clock only starts when you send your first message — not when you open the chat window." },
                },
                {
                  "@type": "Question",
                  "name": "How many astrologers are available?",
                  "acceptedAnswer": { "@type": "Answer", "text": "AstroKuber has multiple verified astrologers, each specialising in a specific classical technique — Parashari, Jaimini, KP Sub-Lord, R.G. Rao Nadi, Tajaka annual reading, D9 Navamsa marriage, D10 Dasamsa career, Kalachakra Dasha, Bhrigu Bindu, Vastu, Numerology and Palmistry." },
                },
                {
                  "@type": "Question",
                  "name": "Are these real astrologers or just AI?",
                  "acceptedAnswer": { "@type": "Answer", "text": "Every astrologer is an AI specialist powered by Claude Sonnet 4.5, wired to a specific Vedic technique and grounded in your Swiss-Ephemeris-computed kundali. Answers are precise, mathematically transparent and never speculative." },
                },
                {
                  "@type": "Question",
                  "name": "Can I ask about someone else's chart?",
                  "acceptedAnswer": { "@type": "Answer", "text": "Yes. Every AI chat opens with a 'Whose chart shall we read?' prompt — choose your own, or enter someone else's birth details to read their chart. You can switch back to your own chart any time." },
                },
                {
                  "@type": "Question",
                  "name": "Is the chat secure and private?",
                  "acceptedAnswer": { "@type": "Answer", "text": "All chats are encrypted in transit. Your birth details and chart are visible only to you. We never share readings with third parties." },
                },
                {
                  "@type": "Question",
                  "name": "What can I ask the astrologer?",
                  "acceptedAnswer": { "@type": "Answer", "text": "Anything — marriage timing, career direction, current dasha effects, manglik / kalsarp / sade-sati remedies, business muhurat, baby naming, name correction, past-life reading, or your annual Varshaphal forecast. Each specialist responds within their classical technique." },
                },
              ],
            },
          ],
        }}
      />
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 pt-6 sm:pt-8 pb-24">
        {/* ───────── Compact conversion hero — mobile: slim single-row
            ribbon so 2-3 astrologer cards land above the fold. Desktop:
            full expanded hero with headline + subtitle + CTA. ───────── */}
        <section className="relative overflow-hidden rounded-2xl border border-soft" data-testid="astrologers-hero">
          {/* Cinematic cosmic backdrop — only for US visitors w/ Cosmic Night theme.
              Replaces the default amber-brown gradient with the NASA scene + Ken-Burns. */}
          <CosmicNightHero intensity="light" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#1A1410] via-[#2A1C12] to-[#3A2515] cosmic-hide" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_15%,rgba(255,184,0,0.18),transparent_55%)] cosmic-hide" />

          {/* ─── MOBILE: slim horizontal ribbon (≤ sm) ─── */}
          <a
            href="#directory"
            className="sm:hidden relative flex items-center gap-2 px-3 py-2.5"
            data-testid="hero-primary-cta"
          >
            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FFB800]/15 border border-[#FFB800]/30 text-gold text-[10px] font-semibold tracking-wide flex-shrink-0">
              <Lightning size={10} weight="fill" /> &lt; 30s
            </div>
            <div className="flex-1 min-w-0 text-white text-[13px] font-medium truncate">
              {firstChatFree
                ? <><span className="text-gold font-bold">First 3 min FREE</span> · chat now</>
                : <>Chat with a verified specialist</>}
            </div>
            <span className="inline-flex items-center justify-center gap-1 bg-white text-[#1A1410] font-bold text-[11px] rounded-full px-2.5 py-1 flex-shrink-0 shadow">
              Start <ArrowRight size={11} weight="bold" />
            </span>
          </a>

          {/* ─── TABLET+ (sm and up): full hero ─── */}
          <div className="hidden sm:flex relative p-5 sm:px-8 sm:py-7 lg:px-12 lg:py-9 flex-col lg:flex-row lg:items-center lg:justify-between gap-4 lg:gap-8">
            <div className="lg:max-w-3xl">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FFB800]/15 border border-[#FFB800]/30 text-gold text-[11px] font-medium tracking-wide" data-testid="hero-pill">
                <Lightning size={12} weight="fill" /> Avg response &lt; 30 seconds
              </div>
              <h1 className="font-serif-display text-2xl sm:text-3xl lg:text-5xl mt-2.5 sm:mt-3 font-light text-white leading-[1.15]">
                {isUS ? T.heroH1 : (<>Talk to <span className="text-gold">Expert Vedic Specialist</span> &amp; Get Life Answers within Seconds</>)}
              </h1>
              <p className="mt-3 text-white/70 text-sm sm:text-base leading-relaxed max-w-2xl">
                {isUS ? T.heroSub : (firstChatFree ? (
                  <>Your <strong className="text-white">first 3 minutes are on us</strong> — chat with any AI specialist below and get answers grounded in your own kundali. Just open a chat.</>
                ) : (
                  <>Verified specialists — each reading grounded in your own kundali. Just open a chat.</>
                ))}
              </p>
            </div>
            <a href="#directory" className="inline-flex items-center justify-center gap-2 bg-white hover:bg-white/90 text-[#1A1410] font-medium px-5 py-3 rounded-full shadow-lg transition-transform hover:scale-[1.02] flex-shrink-0 self-start lg:self-auto">
              <ChatCircleDots size={16} weight="fill" />
              {T.ctaPrimary}
              <ArrowRight size={14} />
            </a>
          </div>
        </section>

        {/* ───────── US category quick-filters (only for US audience) ─────────
            Compact horizontal chip row. Scrolls horizontally on mobile so it
            stays a single row and never pushes the first astrologer card
            below the fold. Each chip is color-themed to its archetype:
            gold=brand · violet=psychic/mystical · rose=tarot · saffron=vedic
            · teal=past-life · pink=soulmate. */}
        {isUS && (() => {
          // theme palette: [activeBg+text, inactiveBg, inactiveBorder, inactiveText]
          const THEMES = {
            gold:    { bg: "#D4AF37", text: "#1A1410", soft: "#FFF6E0", border: "#E8C77A", soft_text: "#7A5A12" },
            violet:  { bg: "#7C3AED", text: "#FFFFFF", soft: "#F1E8FF", border: "#C8AEFF", soft_text: "#4C1D95" },
            rose:    { bg: "#BE123C", text: "#FFF1F2", soft: "#FFE4E6", border: "#FBB6C2", soft_text: "#9F1239" },
            saffron: { bg: "#EA580C", text: "#FFF7ED", soft: "#FFEDD5", border: "#FDBA74", soft_text: "#9A3412" },
            emerald: { bg: "#059669", text: "#ECFDF5", soft: "#D1FAE5", border: "#6EE7B7", soft_text: "#065F46" },
            teal:    { bg: "#0F766E", text: "#F0FDFA", soft: "#CCFBF1", border: "#5EEAD4", soft_text: "#115E59" },
            pink:    { bg: "#DB2777", text: "#FFF1F8", soft: "#FCE7F3", border: "#F9A8D4", soft_text: "#9D174D" },
          };
          return (
            <div className="mt-4 -mx-3 px-3 sm:mx-0 sm:px-0" data-testid="us-category-bar">
              <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto no-scrollbar pb-1.5">
                {US_CATEGORIES.map((c) => {
                  const active = category === c.key;
                  const t = THEMES[c.color] || THEMES.gold;
                  const style = active
                    ? { backgroundColor: t.bg, color: t.text, borderColor: t.bg,
                        boxShadow: `0 2px 8px -1px ${t.bg}55` }
                    : { backgroundColor: t.soft, color: t.soft_text, borderColor: t.border };
                  return (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => update("category", c.key === "all" ? "" : c.key)}
                      className="flex-shrink-0 whitespace-nowrap text-[11px] sm:text-xs font-semibold px-3 py-1.5 rounded-full border transition-all hover:-translate-y-px"
                      style={style}
                      data-testid={`us-cat-${c.key}`}
                      aria-pressed={active}
                    >
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* ───────── Filter Bar — single compact row on every viewport ───────── */}
        <div id="directory" className="mt-3 sm:mt-4 scroll-mt-4">
          <div className="card-surface rounded-2xl p-2.5 sm:p-3 border border-soft">
            <div className="grid grid-cols-[0.7fr_1fr_1fr_1fr] sm:grid-cols-[1fr_1fr_1fr_1fr] gap-1.5 sm:gap-2">
              {/* Search w/ typeahead suggestions — the dropdown is portalled
                  to document.body so it escapes any stacking context formed
                  by sibling card transitions and always sits on top. */}
              <div ref={searchBoxRef} className="relative">
                <div className="flex items-center gap-1.5 bg-bg rounded-full pl-3 pr-2 border border-soft" data-testid="search-box">
                  <MagnifyingGlass size={14} className="text-muted flex-shrink-0" />
                  <input
                    id="search-input"
                    type="text"
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setSuggestOpen(true); setHighlight(-1); }}
                    onFocus={() => setSuggestOpen(true)}
                    onKeyDown={handleKeyDown}
                    placeholder="Search name"
                    aria-label="Search by name"
                    aria-autocomplete="list"
                    aria-expanded={suggestOpen && suggestions.length > 0}
                    className="bg-transparent outline-none flex-1 text-xs sm:text-sm py-2 min-w-0 placeholder:text-muted"
                    data-testid="search-input"
                  />
                  {query && (
                    <button
                      type="button"
                      onClick={() => { setQuery(""); commitSearch(""); }}
                      aria-label="Clear search"
                      className="text-muted hover:text-foreground flex-shrink-0 p-0.5"
                      data-testid="search-clear"
                    >
                      <X size={12} weight="bold" />
                    </button>
                  )}
                </div>
              </div>
              {suggestOpen && popRect && createPortal(
                suggestions.length > 0 ? (
                  <div
                    ref={suggestionsRef}
                    className="fixed card-surface rounded-2xl border border-soft shadow-2xl max-h-80 overflow-y-auto py-1.5"
                    style={{
                      top: popRect.top,
                      left: popRect.left,
                      width: popRect.width,
                      minWidth: 220,
                      zIndex: 9999,
                      backgroundColor: "var(--surface)",
                    }}
                    role="listbox"
                    data-testid="search-suggestions"
                  >
                    {suggestions.map((a, idx) => (
                      <Link
                        key={a.astrologer_id}
                        to={`/astrologers/${a.astrologer_id}`}
                        onMouseEnter={() => setHighlight(idx)}
                        onClick={() => setSuggestOpen(false)}
                        role="option"
                        aria-selected={highlight === idx}
                        className={`flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                          highlight === idx ? "bg-[#FFB800]/10" : "hover:bg-[#FFB800]/8"
                        }`}
                        data-testid={`suggest-${a.astrologer_id}`}
                      >
                        <img
                          src={resolveAssetUrl(a.avatar)}
                          alt=""
                          className="w-7 h-7 rounded-full object-cover flex-shrink-0"
                          loading="lazy"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="truncate font-medium text-xs sm:text-sm">{a.name}</div>
                          <div className="truncate text-[10px] sm:text-[11px] text-muted">
                            {a.specialization} · {(a.languages || [])[0] || "—"}
                          </div>
                        </div>
                        {a.is_ai && (
                          <Sparkle size={11} weight="fill" className="text-gold flex-shrink-0" />
                        )}
                      </Link>
                    ))}
                  </div>
                ) : query.trim() ? (
                  <div
                    ref={suggestionsRef}
                    className="fixed card-surface rounded-2xl border border-soft shadow-2xl p-3 text-xs text-muted"
                    style={{
                      top: popRect.top,
                      left: popRect.left,
                      width: popRect.width,
                      minWidth: 220,
                      zIndex: 9999,
                      backgroundColor: "var(--surface)",
                    }}
                    data-testid="search-no-suggestions"
                  >
                    No astrologer matches &ldquo;{query}&rdquo;
                  </div>
                ) : null,
                document.body
              )}
              {/* Expertise */}
              <select value={spec} onChange={(e) => update("spec", e.target.value)}
                      aria-label="Expertise"
                      className="bg-bg rounded-full px-2.5 sm:px-3 py-2 text-xs sm:text-sm border border-soft cursor-pointer min-w-0" data-testid="filter-spec">
                {SPECS.map((s) => <option key={s} value={s} className="bg-bg">{s === "All" ? "Expertise" : s}</option>)}
              </select>
              {/* Language */}
              <select value={lang} onChange={(e) => update("lang", e.target.value)}
                      aria-label="Language"
                      className="bg-bg rounded-full px-2.5 sm:px-3 py-2 text-xs sm:text-sm border border-soft cursor-pointer min-w-0" data-testid="filter-lang">
                {LANGS.map((s) => <option key={s} value={s} className="bg-bg">{s === "All" ? "Language" : s}</option>)}
              </select>
              {/* Experience */}
              <select value={exp} onChange={(e) => update("exp", e.target.value)}
                      aria-label="Experience"
                      className="bg-bg rounded-full px-2.5 sm:px-3 py-2 text-xs sm:text-sm border border-soft cursor-pointer min-w-0" data-testid="filter-exp">
                {EXP_BUCKETS.map((b) => <option key={b.value} value={b.value} className="bg-bg">{b.value === 0 ? "Experience" : b.label}</option>)}
              </select>
            </div>
            {!loading && hasActiveFilters && (
              <div className="text-[11px] text-muted mt-2 flex items-center gap-2.5" data-testid="result-count">
                <button onClick={clearFilters} className="text-gold underline decoration-dotted hover:text-[#FFC933]" data-testid="clear-filters-btn">
                  Clear filters
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ───────────── Astrologer Grid ───────────── */}
        <div className="mt-8">
          {loading ? (
            <div className="text-muted py-20 text-center">{T.loadingMsg}</div>
          ) : displayList.length === 0 ? (
            <div className="text-muted py-20 text-center" data-testid="no-results">
              {T.emptyMsg}
              <button onClick={clearFilters} className="block mx-auto mt-4 text-gold underline decoration-dotted" data-testid="empty-clear-btn">
                {T.emptyCta}
              </button>
            </div>
          ) : (
            <div className="grid gap-4 sm:gap-5 md:grid-cols-2" data-testid="astrologer-grid">
              {displayList.map((a) => {
                const avail = isAvailable(a);
                const liveHuman = !a.is_ai && (a.chat_enabled || a.call_enabled);
                const actuallyBusy = !a.is_ai && a.is_busy;
                const rate = Number(a.rate_per_min) || 0;
                // Marketing "was" price → 2.85× for AI, 1.67× for humans.
                // Rounded to nearest 5 for a clean sticker feel.
                const wasRaw = a.is_ai ? rate * 2.85 : rate * 1.67;
                const wasPrice = Math.max(rate + 1, Math.round(wasRaw / 5) * 5);
                const pctOff = wasPrice > rate ? Math.round((1 - rate / wasPrice) * 100) : 0;
                const chatsK = displayedReviewsCount(a.astrologer_id);
                const totalChatsLabel = chatsK >= 1000
                  ? `${(chatsK / 1000).toFixed(1).replace(/\.0$/, "")}K+`
                  : `${chatsK}+`;
                const showFree = a.is_ai && firstChatFree;
                return (
                <Link
                  to={`/astrologers/${a.astrologer_id}`}
                  key={a.astrologer_id}
                  className="astro-tile group relative flex items-stretch gap-3 sm:gap-4 p-3 sm:p-4 rounded-3xl bg-surface shadow-[0_6px_18px_rgba(63,45,27,.10)] ring-1 ring-amber-200/60 transition-all hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(63,45,27,.16)] overflow-hidden"
                  data-testid={`astrologer-card-${a.astrologer_id}`}
                >
                  {/* Gold gradient hairline on top edge */}
                  <span
                    aria-hidden
                    className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-[#F6C97C] via-[#E89E4B] to-[#C58F3B]"
                  />

                  {/* ── Left: Avatar frame ─────────────────────────── */}
                  <div className="relative flex-shrink-0 w-24 sm:w-28 md:w-32 self-center">
                    <div className="rounded-2xl p-[2.5px] bg-gradient-to-br from-[#F6C97C] via-[#E89E4B] to-[#C58F3B] shadow-[0_3px_10px_rgba(184,122,42,.35)]">
                      <div className="rounded-[14px] overflow-hidden bg-amber-50 aspect-square">
                        <img
                          src={resolveAssetUrl(a.avatar)}
                          alt={a.name}
                          loading="lazy"
                          className="w-full h-full object-cover object-top transition-transform group-hover:scale-[1.04]"
                        />
                      </div>
                    </div>
                    {/* Total-chats social-proof chip */}
                    <div
                      className="absolute -bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] sm:text-[11px] font-bold px-2 py-0.5 rounded-full shadow ring-1 ring-amber-300/60 bg-surface"
                      data-testid={`chats-${a.astrologer_id}`}
                    >
                      Chats <span className="text-emerald-600 font-extrabold">{totalChatsLabel}</span>
                    </div>
                  </div>

                  {/* ── Middle: Info column ───────────────────────── */}
                  <div className="flex-1 min-w-0 flex flex-col justify-center pr-1">
                    <div className="flex items-start gap-1.5 min-w-0">
                      <div
                        className="font-serif-display text-[20px] sm:text-[22px] leading-tight truncate"
                        data-testid={`name-${a.astrologer_id}`}
                      >
                        {a.name}
                      </div>
                      {!a.is_ai && (
                        <span
                          className="flex-shrink-0 mt-0.5 w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-gradient-to-br from-[#F6C97C] to-[#C58F3B] text-white text-[9px] sm:text-[10px] font-extrabold inline-flex items-center justify-center shadow ring-1 ring-white/50"
                          title="KYC verified"
                          aria-label="Verified"
                        >✓</span>
                      )}
                    </div>
                    {(a.technique || a.specialization) && (
                      <div className="mt-1.5">
                        <span
                          className="astro-tag inline-block px-2.5 py-0.5 rounded-full text-[11px] sm:text-xs font-bold bg-gradient-to-br from-[#FDE1B4] to-[#F6C97C] text-[#3F2D1B] shadow-sm"
                          data-testid={`technique-${a.astrologer_id}`}
                        >
                          {a.technique || a.specialization}
                        </span>
                      </div>
                    )}
                    <div className="mt-1.5 text-[11px] sm:text-xs font-semibold flex items-center gap-1.5">
                      <Star size={12} weight="fill" className="text-[#E89E4B]" />
                      <span data-testid={`reviews-${a.astrologer_id}`}>
                        {displayedRating(a.astrologer_id)}
                        <span className="text-muted font-medium"> ({chatsK.toLocaleString("en-IN")})</span>
                      </span>
                    </div>
                    <div className="mt-0.5 text-[11px] sm:text-xs text-muted font-medium truncate">
                      {a.experience_years} yrs · {a.languages.join(", ")}
                    </div>
                    {showFree && (
                      <div
                        className="mt-1.5 inline-flex items-center gap-1 self-start px-2 py-0.5 rounded-full text-[10px] sm:text-[11px] font-extrabold tracking-wide bg-emerald-500 text-white shadow ring-1 ring-emerald-300/60"
                        data-testid={`free-ribbon-${a.astrologer_id}`}
                      >
                        <Sparkle size={10} weight="fill" /> FIRST 3 MIN FREE
                      </div>
                    )}
                  </div>

                  {/* ── Right: Price + CTA ───────────────────────── */}
                  <div className="flex-shrink-0 flex flex-col items-end justify-between gap-1.5 min-w-[92px] sm:min-w-[110px]">
                    {/* AI ASTRO / LIVE tag */}
                    {a.is_ai ? (
                      a.haiku_test_variant ? (
                        <span
                          className="astro-ai-badge inline-block px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] font-extrabold tracking-[.1em] bg-gradient-to-br from-purple-400 to-fuchsia-600 text-white shadow-sm"
                          data-testid={`haiku-badge-${a.astrologer_id}`}
                          title="A/B test variant powered by Claude Haiku 4.5"
                        >
                          HAIKU
                        </span>
                      ) : (
                        <span
                          className="astro-ai-badge inline-block px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] font-extrabold tracking-[.1em] bg-gradient-to-br from-[#FDE1B4] to-[#F6C97C] text-[#3F2D1B] shadow-sm"
                          data-testid={`badge-${a.astrologer_id}`}
                        >
                          AI ASTRO
                        </span>
                      )
                    ) : (
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] font-extrabold tracking-[.08em] shadow-sm ${
                          actuallyBusy
                            ? "bg-amber-500 text-white"
                            : liveHuman
                            ? "bg-emerald-500 text-white"
                            : avail
                            ? "bg-emerald-500 text-white"
                            : "bg-red-500 text-white"
                        }`}
                        data-testid={`availability-${a.astrologer_id}`}
                        data-status={actuallyBusy ? "busy" : liveHuman ? "live" : avail ? "available" : "offline"}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full bg-white ${avail || liveHuman ? "animate-pulse" : ""}`} />
                        {actuallyBusy ? "BUSY" : liveHuman ? "LIVE" : avail ? "ONLINE" : "OFFLINE"}
                      </span>
                    )}

                    <div className="text-right leading-none">
                      <div className="font-serif-display text-[26px] sm:text-[30px] leading-none">
                        {formatPrice(rate)}
                        <span className="text-[11px] sm:text-xs text-muted font-semibold align-baseline">/min</span>
                      </div>
                      {pctOff > 0 && (
                        <div className="mt-1 text-[10px] sm:text-[11px] leading-none">
                          <span className="text-muted line-through font-semibold">
                            {formatPrice(wasPrice)}
                          </span>
                          <span className="ml-1 inline-block bg-emerald-500 text-white font-extrabold rounded-full px-1.5 py-[1px] text-[9px] sm:text-[10px] align-baseline">
                            {pctOff}% off
                          </span>
                        </div>
                      )}
                    </div>

                    {/* CTA — AI: single Chat; Human: Call + Chat stacked */}
                    {a.is_ai ? (
                      <button
                        type="button"
                        onClick={(e) => startChatFromCard(e, a)}
                        className="w-full inline-flex items-center justify-center gap-1 bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-white font-extrabold text-[12px] sm:text-[13px] rounded-xl px-3 py-2 shadow-[0_4px_10px_rgba(22,163,74,.35)] transition-all"
                        data-testid={`chat-cta-${a.astrologer_id}`}
                      >
                        <ChatCircleDots size={14} weight="fill" />
                        {showFree ? "Free Chat" : "Chat Now"}
                      </button>
                    ) : actuallyBusy ? (
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/astrologers/${a.astrologer_id}`); }}
                        className="w-full inline-flex items-center justify-center gap-1 bg-amber-500 hover:bg-amber-600 text-white font-extrabold text-[12px] sm:text-[13px] rounded-xl px-3 py-2 shadow-[0_4px_10px_rgba(245,158,11,.35)] transition-all"
                        data-testid={`busy-cta-${a.astrologer_id}`}
                        aria-label={`${a.name} is busy — tap to join queue`}
                      >
                        <Clock size={14} weight="fill" />
                        Join Queue
                      </button>
                    ) : (
                      <div className="w-full flex gap-1.5">
                        <button
                          type="button"
                          onClick={(e) => startCallFromCard(e, a)}
                          disabled={!a.call_enabled}
                          className={`flex-1 inline-flex items-center justify-center gap-1 font-extrabold text-[11px] sm:text-[12px] rounded-xl px-1.5 py-2 shadow transition-all ${
                            a.call_enabled
                              ? "bg-surface text-emerald-700 ring-2 ring-emerald-300 hover:ring-emerald-400"
                              : "bg-surface text-muted ring-2 ring-soft cursor-not-allowed opacity-60"
                          }`}
                          data-testid={`call-cta-${a.astrologer_id}`}
                          aria-label={`Call ${a.name}`}
                        >
                          <Phone size={12} weight="fill" />
                          Call
                        </button>
                        <button
                          type="button"
                          onClick={(e) => startChatFromCard(e, a)}
                          disabled={!a.chat_enabled}
                          className={`flex-1 inline-flex items-center justify-center gap-1 font-extrabold text-[11px] sm:text-[12px] rounded-xl px-1.5 py-2 shadow transition-all ${
                            a.chat_enabled
                              ? "bg-emerald-500 hover:bg-emerald-600 text-white shadow-[0_3px_8px_rgba(22,163,74,.35)]"
                              : "bg-emerald-500/30 text-emerald-100/70 cursor-not-allowed"
                          }`}
                          data-testid={`chat-cta-${a.astrologer_id}`}
                          aria-label={`Chat with ${a.name}`}
                        >
                          <ChatCircleDots size={12} weight="fill" />
                          Chat
                        </button>
                      </div>
                    )}
                  </div>
                </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </PageShell>
  );
}
